import { DateTime } from 'luxon'

const ZONE = 'Europe/Paris'

export const WEEK_PRESETS = [
  'last_week',
  'week_minus_2',
  'week_minus_3',
  'last_4_weeks',
  'current_week',
] as const

export type WeekPreset = (typeof WEEK_PRESETS)[number]

export interface WeekRange {
  startDate: string
  endDate: string
}

export interface WeekPeriod extends WeekRange {
  preset?: WeekPreset
}

export const parseDay = (date: string) => DateTime.fromISO(date, { zone: ZONE })

const toIso = (date: DateTime) => date.toISODate()!

const pastWeeks = (thisMonday: DateTime, weeksAgo: number, weekCount: number): WeekRange => {
  const start = thisMonday.minus({ weeks: weeksAgo })
  return {
    startDate: toIso(start),
    endDate: toIso(start.plus({ weeks: weekCount, days: -1 })),
  }
}

const PAST_PRESETS: Record<Exclude<WeekPreset, 'current_week'>, [number, number]> = {
  last_week: [1, 1],
  week_minus_2: [2, 1],
  week_minus_3: [3, 1],
  last_4_weeks: [4, 4],
}

export function weekPresetRange(preset: WeekPreset, today: string): WeekRange {
  const day = parseDay(today)
  const thisMonday = day.startOf('week')
  if (preset !== 'current_week') return pastWeeks(thisMonday, ...PAST_PRESETS[preset])
  const yesterday = day.minus({ days: 1 })
  return {
    startDate: toIso(thisMonday),
    endDate: toIso(yesterday < thisMonday ? thisMonday : yesterday),
  }
}

export function weekRangeFromClicks(today: string, first: string, second?: string): WeekRange {
  const a = parseDay(first)
  const b = second === undefined ? a : parseDay(second)
  const [earliest, latest] = a <= b ? [a, b] : [b, a]
  const endDate = toIso(latest.endOf('week'))
  const lastDay = weekPresetRange('current_week', today).endDate
  return {
    startDate: toIso(earliest.startOf('week')),
    endDate: endDate > lastDay ? lastDay : endDate,
  }
}

const month = (date: DateTime) => date.setLocale('fr').toFormat('LLL')

const dayMonth = (date: DateTime) => `${date.day} ${month(date)}`

const daysLabel = (start: DateTime, end: DateTime) => {
  if (start.hasSame(end, 'day')) return dayMonth(start)
  if (start.hasSame(end, 'month')) return `${start.day}-${end.day} ${month(end)}`
  return `${dayMonth(start)}-${dayMonth(end)}`
}

export function weekPeriodParts({ startDate, endDate }: WeekRange): {
  weeks: string
  days: string
} {
  const start = parseDay(startDate)
  const end = parseDay(endDate)
  const weeks = start.hasSame(end, 'week')
    ? `S${start.weekNumber}`
    : `S${start.weekNumber}-S${end.weekNumber}`
  return { weeks, days: daysLabel(start, end) }
}
